import { useState, useEffect, type FC } from "react";
import { SidebarContent, SidebarHeader } from "@capacit-com/lara-law-shared-ui";
import { Tabs } from "@capacit-com/lara-law-shared-ui";
import DocumentTabTriggers, { DocumentTab } from "./document-tab-triggers";
import ChatReferencesTab from "./tabs/chat-reference-tab";
import VersionHistoryTab from "./tabs/version-history-tab";
import DocumentReferencesTab from "./tabs/document-references-tab";
import {
  ChatReferencesTabSkeleton,
  VersionHistoryTabSkeleton,
  DocumentReferencesTabSkeleton,
  DocumentMetadataTabSkeleton,
  TableOfContentsTabSkeleton,
} from "./tabs/skeleton-tabs";
import DocumentMetadataTab from "./tabs/document-metadata-tab";
import { Legislation } from "@/models/legislation/Legislation";
import { TableOfContentsItem } from "@/models/table-of-contents";
import TableOfContentsTab from "./tabs/table-of-contents-tab";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import useDocumentSidebarTabStore from "@/stores/document-sidebar-tab.store";
import { resolveDocumentSidebarTab } from "./resolveDocumentSidebarTab";
import type { DocumentReferenceTarget } from "@/lib/documentReferenceRequest";

type DocumentSidebarProps = {
  /** Contextual override (e.g. chat → Chat references). Not the product default. */
  contextualDefault?: DocumentTab;
  availableTabs: DocumentTab[];
  documentId?: string;
  /**
   * Stable document identity for resetting in-session tab selection.
   * Must not change across revisions of the same document (avoid blob/revision ids).
   */
  selectionResetKey?: string;
  /** blob_id used as key for TOC fetch (one TOC per document revision) */
  blobId?: string;
  /** Current revision version number being viewed (for filtering links) */
  currentRevisionVersion?: number;
  isLoading?: boolean;
  legislation?: Legislation | null;
  /** Workspace document for metadata tab (settings-defined metadata) */
  workspaceDocument?: WorkspaceDocument | null;
  /** Optional pre-loaded TOC items (e.g. mock data for dev preview) */
  tocItems?: TableOfContentsItem[];
  /**
   * The open document, so the references tab can offer "Opret reference" for it.
   * Omit to keep the tab read-only.
   */
  createReferenceTarget?: DocumentReferenceTarget;
};

const DocumentSidebar: FC<DocumentSidebarProps> = ({
  contextualDefault,
  availableTabs,
  documentId,
  selectionResetKey,
  blobId,
  currentRevisionVersion,
  isLoading = false,
  legislation,
  workspaceDocument,
  tocItems,
  createReferenceTarget,
}) => {
  const lastUsedTab = useDocumentSidebarTabStore((state) => state.lastUsedTab);
  const setLastUsedTab = useDocumentSidebarTabStore(
    (state) => state.setLastUsedTab
  );
  const [userSelectedTab, setUserSelectedTab] = useState<DocumentTab | null>(
    null
  );

  // New document (or chat in/out) re-resolves; revision/blob changes must not.
  useEffect(() => {
    setUserSelectedTab(null);
  }, [selectionResetKey, contextualDefault]);

  const activeTab =
    userSelectedTab && availableTabs.includes(userSelectedTab)
      ? userSelectedTab
      : resolveDocumentSidebarTab({
          contextualDefault,
          lastUsed: lastUsedTab,
          availableTabs,
        });

  const handleTabChange = (value: string) => {
    const tab = value as DocumentTab;
    setUserSelectedTab(tab);
    setLastUsedTab(tab);
  };

  const isTabAvailable = (tab: DocumentTab) => availableTabs.includes(tab);

  return (
    <>
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full h-full"
      >
        <SidebarHeader className="h-12 border-b">
          <DocumentTabTriggers
            activeTab={activeTab}
            availableTabs={availableTabs}
          />
        </SidebarHeader>

        <SidebarContent className="gap-0">
          {isLoading ? (
            <>
              {isTabAvailable(DocumentTab.CHAT_REFERENCES) &&
                activeTab === DocumentTab.CHAT_REFERENCES && (
                  <ChatReferencesTabSkeleton />
                )}
              {isTabAvailable(DocumentTab.VERSION_HISTORY) &&
                activeTab === DocumentTab.VERSION_HISTORY && (
                  <VersionHistoryTabSkeleton />
                )}
              {isTabAvailable(DocumentTab.DOCUMENT_REFERENCES) &&
                activeTab === DocumentTab.DOCUMENT_REFERENCES && (
                  <DocumentReferencesTabSkeleton />
                )}
              {isTabAvailable(DocumentTab.DOCUMENT_METADATA) &&
                activeTab === DocumentTab.DOCUMENT_METADATA && (
                  <DocumentMetadataTabSkeleton />
                )}
              {isTabAvailable(DocumentTab.TABLE_OF_CONTENTS) &&
                activeTab === DocumentTab.TABLE_OF_CONTENTS && (
                  <TableOfContentsTabSkeleton />
                )}
            </>
          ) : (
            <>
              {isTabAvailable(DocumentTab.CHAT_REFERENCES) &&
                activeTab === DocumentTab.CHAT_REFERENCES && (
                  <ChatReferencesTab />
                )}
              {isTabAvailable(DocumentTab.VERSION_HISTORY) &&
                activeTab === DocumentTab.VERSION_HISTORY && (
                  <VersionHistoryTab />
                )}
              {isTabAvailable(DocumentTab.DOCUMENT_REFERENCES) &&
                activeTab === DocumentTab.DOCUMENT_REFERENCES &&
                (documentId ? (
                  <DocumentReferencesTab
                    key={documentId}
                    documentId={documentId}
                    currentRevisionVersion={currentRevisionVersion}
                    createReferenceTarget={createReferenceTarget}
                  />
                ) : (
                  <DocumentReferencesTabSkeleton />
                ))}
              {isTabAvailable(DocumentTab.DOCUMENT_METADATA) &&
                activeTab === DocumentTab.DOCUMENT_METADATA &&
                (legislation ? (
                  <DocumentMetadataTab legislation={legislation} />
                ) : workspaceDocument ? (
                  <DocumentMetadataTab workspaceDocument={workspaceDocument} />
                ) : (
                  <DocumentMetadataTabSkeleton />
                ))}
              {isTabAvailable(DocumentTab.TABLE_OF_CONTENTS) &&
                activeTab === DocumentTab.TABLE_OF_CONTENTS && (
                  <TableOfContentsTab blobId={blobId} tocItems={tocItems} />
                )}
            </>
          )}
        </SidebarContent>
      </Tabs>
    </>
  );
};

export default DocumentSidebar;
